import './widget.css';

import {
  IonButton,
  IonPopover,
  IonRippleEffect,
  isPlatform,
} from '@ionic/react';
import { FieldConfig, useField } from 'formik';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TwitterPicker } from 'react-color';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { PopoverState } from '../../../../../components/ui/ui-interfaces';
import { useIsMounted } from '../../../../../util/hooks';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { WidgetLabel } from './widget-label';
import { WidgetProps, WidgetRef, createWidgetExtension } from './widget.types';

const ColorPickerInput = (props: WidgetProps & FieldConfig): ReactElement => {
  const {
    label,
    description,
    ionItemProps,
    extensions,
    disabled,
    subtitle,
    subtitleColor,
    isNested,
    isPaywalled,
    transparentBackground,
    ...otherProps
  } = props;

  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);

  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  } as PopoverState);

  const inputRef = useRef<HTMLIonInputElement>(null);
  const ionButtonRef = useRef<HTMLIonButtonElement>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    includeClasses: ['color-picker-widget'],
    isNested,
    isRippleUtilized: true,
    transparentBackground,
    isPaywalled,
  });

  const isMounted = useIsMounted();

  const showOptions = (
    e: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();
    if (isMounted()) {
      setShowPopover({ showPopover: true, event: e as unknown as Event });
    }
  };

  const hideOptions = () => {
    if (isMounted()) {
      setShowPopover({ showPopover: false, event: undefined });
    }
  };

  const isIOS = isPlatform('ios');

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      onClick={async () => {
        ionButtonRef.current?.click();
      }}
      button={rightElement ? false : !isIOS}
      {...restIonItemProps}
    >
      <WidgetLabel
        label={label}
        description={description}
        error={meta.error}
        forceDisable={isDisabled}
        subtitle={subtitle}
        subtitleColor={subtitleColor}
      />
      <IonButton
        slot="end"
        ref={ionButtonRef}
        fill={'clear'}
        onClick={(e) => {
          if (isPaywalled) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();
          e.persist();
          showOptions(e);
        }}
      >
        <svg width={20} height={20}>
          <rect width="100%" height="100%" fill={field.value ?? 'white'} />
        </svg>
      </IonButton>
      {rightElement}
      <IonPopover
        data-cy="color-picker-popover"
        event={popoverState.event}
        isOpen={popoverState.showPopover}
        onDidDismiss={() => {
          hideOptions();
        }}
        showBackdrop={true}
      >
        <TwitterPicker
          color={field.value}
          onChange={(color) => {
            helper.setValue(color.hex);
            hideOptions();
          }}
          width="100%"
        />
      </IonPopover>
      {!isIOS && <IonRippleEffect />}
    </IonItem>
  );
};

export { ColorPickerInput };
